import React, { useEffect, useState, useCallback } from "react";
import { getData } from "../../../utils/getData";
import StandingTable from "./Standings";
import Matches from "../../common/football/Macthes";
import TopScorers from "../../common/TopScorers";
import TopAssists from "./TopAssists";
import News from "../../common/News";
import NoDataFound from "../../common/NoDataFound";
import { loadStyles } from "../../../utils/cssLoader";

// Tab configuration for better maintainability
const TAB_CONFIG = {
  "league-upcoming": {
    component: "matches",
    matchType: "upcoming",
  },
  "league-finished": {
    component: "matches",
    matchType: "finished",
  },
  "league-results": {
    component: "matches",
    matchType: "finished",
  },
  "league-table": {
    component: "standings",
  },
  "league-top-scorers": {
    component: "topScorers",
  },
  "league-top-assists": {
    component: "topAssists",
  },
  "league-news": {
    component: "news",
    newsType: "league",
  },
  "league-stats": {
    component: "stats",
  },
};

const LeaguePage = ({
  data,
  baseUrl,
  difference,
  sport = "football",
  dataId,
  loadCSS,
  headData,
  setDataId,
  setDataType,
  setHead,
  setLeagueId,
  websiteBaseUrl,
  dataPath = "football",
  dataClient = false,
}) => {
  const [active, setActive] = useState(null);
  const [activeTabData, setActiveTabData] = useState(null);
  const [tabs, setTabs] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  // Initialize tabs from headData
  useEffect(() => {
    if (headData?.tabs?.length > 0) {
      const leagueTabs = headData.tabs.map((tab) => ({
        key: `league-${tab?.replaceAll("_", "-")}`,
        label: tab.replaceAll("_", " "),
      }));
      setTabs(leagueTabs);
      setActive({
        key: `league-${headData.tabs[0]?.replaceAll("_", "-")}`,
        label: headData.tabs[0].replaceAll("_", " "),
      });
    }
  }, [headData]);

  // Set initial data
  useEffect(() => {
    if (data) {
      setActiveTabData(data);
    }
  }, [data]);

  useEffect(() => {
    if (loadCSS === "true") {
      loadStyles("leaguepage");
    }
  }, [loadCSS]);

  // Fetch data for active tab
  const fetchData = useCallback(async () => {
    if (!active?.key) return;

    try {
      setIsLoading(true);
      const tabConfig = TAB_CONFIG[active.key];

      const fetchedData = await getData({
        dataId,
        dataType: active.key,
        baseUrl,
        sport,
        newsType: tabConfig?.newsType || undefined,
      });
      setActiveTabData(fetchedData);
    } catch (error) {
      console.error("Error fetching data:", error);
      setActiveTabData(null);
    } finally {
      setIsLoading(false);
    }
  }, [active?.key, dataId, baseUrl, sport]);

  useEffect(() => {
    if (active) {
      fetchData();
    }
  }, [active, fetchData]);

  // Handle tab click
  const handleTabClick = (key, label) => {
    if (active?.key !== key) {
      setIsLoading(true);
      setActive({ key, label });
      setActiveTabData(null);
    }
  };

  // Render tab content based on active tab
  const renderTabContent = () => {
    if (isLoading) {
      return <div className="loading-state"><h3>Loading...</h3></div>;
    }

    const tabConfig = TAB_CONFIG[active?.key];

    if (!tabConfig) {
      return <NoDataFound />;
    }

    switch (tabConfig.component) {
      case "matches":
        return activeTabData?.data ? (
          <Matches
            data={activeTabData.data}
            difference={difference}
            matchType={tabConfig.matchType}
            loadCSS={loadCSS}
            sport={sport}
            baseUrl={baseUrl}
            setDataId={setDataId}
            setDataType={setDataType}
            setHead={setHead}
            setLeagueId={setLeagueId}
            websiteBaseUrl={websiteBaseUrl}
            dataPath={dataPath}
            dataClient={dataClient}
          />
        ) : (
          <NoDataFound text={`No ${tabConfig.matchType} matches found`} />
        );

      case "standings":
        return activeTabData?.data?.football_tournament_tables ? (
          <StandingTable
            data={activeTabData.data.football_tournament_tables}
            loadCSS={loadCSS}
          />
        ) : (
          <NoDataFound text="No standings data available" />
        );

      case "topScorers":
        return activeTabData?.data ? (
          <TopScorers
            data={activeTabData.data}
            loadCSS={loadCSS}
            sport={sport}
            websiteBaseUrl={websiteBaseUrl}
            dataPath={dataPath}
            dataClient={dataClient}
          />
        ) : (
          <NoDataFound text="No top scorers data available" />
        );

      case "topAssists":
        return activeTabData?.data ? (
          <TopAssists
            data={activeTabData.data}
            loadCSS={loadCSS}
            sport={sport}
            websiteBaseUrl={websiteBaseUrl}
            dataPath={dataPath}
            dataClient={dataClient}
          />
        ) : (
          <NoDataFound text="No top assists data available" />
        );

      case "news":
        return activeTabData?.data || activeTabData ? (
          <News
            data={activeTabData?.data || activeTabData}
            loadCSS={loadCSS}
            newsType={tabConfig.newsType}
            id={dataId}
            sport={sport}
            baseUrl={baseUrl}
          />
        ) : (
          <NoDataFound text="No news available" />
        );

      case "stats":
        return activeTabData?.data ? (
          <div className="league_stats_wrp">
            {/* Stats component would go here */}
            <pre>{JSON.stringify(activeTabData.data, null, 2)}</pre>
          </div>
        ) : (
          <NoDataFound text="No stats available" />
        );

      default:
        return <NoDataFound />;
    }
  };

  return (
    <div className="league-page">
      <div className="detail_page">
        {tabs.length > 0 && (
          <nav className="inner_tab detail_main_tab">
            <ul>
              {tabs.map(({ key, label }) => (
                <li
                  key={key}
                  className={active?.key === key ? "active" : ""}
                  onClick={() => handleTabClick(key, label)}
                  data-name={key}
                >
                  <button>{label}</button>
                </li>
              ))}
            </ul>
          </nav>
        )}
        <div className="tab_content">
          {renderTabContent()}
        </div>
      </div>
    </div>
  );
};

export default LeaguePage;
